import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Sidebar } from '@/components/Sidebar';
import { BackgroundWaves } from '@/components/BackgroundVideo';
import { useSupabase } from '@/hooks/useSupabase';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { fetchDiscoverDetailContent, DiscoverDetailType, DiscoverDetailContent } from '@/services/discoverService';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Newspaper,
  Tag,
  ArrowUpRight,
  CheckCircle2,
  ExternalLink,
  Quote,
  Sparkles,
  Clock,
} from 'lucide-react';

const SUPPORTED_DETAIL_TYPES: DiscoverDetailType[] = ['news', 'topic', 'weather', 'digest'];

const DiscoverDetail = () => {
  const navigate = useNavigate();
  const { type } = useParams();
  const location = useLocation();
  const { user } = useSupabase();
  const { theme } = useTheme();
  const [systemPrefersDark, setSystemPrefersDark] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const isDarkMode = theme === 'dark' || (theme === 'system' && systemPrefersDark);
  const [isOpen, setIsOpen] = React.useState(false);
  const [detailContent, setDetailContent] = React.useState<DiscoverDetailContent | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const getWeatherArtForCondition = React.useCallback((condition: string) => {
    const c = (condition || '').toLowerCase();
    if (c.includes('thunder') || c.includes('storm')) return '/weather/thunderstorm.png';
    if (c.includes('rain') || c.includes('drizzle') || c.includes('shower')) return '/weather/rain.png';
    if ((c.includes('cloud') && c.includes('sun')) || c.includes('partly')) return '/weather/partly-cloudy.png';
    if (c.includes('cloud') || c.includes('overcast')) return '/weather/cloudy.png';
    if (c.includes('sun') || c.includes('clear')) return '/weather/sunny.png';
    return '/weather/partly-cloudy.png';
  }, []);

  const data = location.state?.data;
  const detailType = type && SUPPORTED_DETAIL_TYPES.includes(type as DiscoverDetailType) ? (type as DiscoverDetailType) : null;

  React.useEffect(() => {
    if (!detailType || !data) {
      setError('Content not available.');
      return;
    }

    setLoading(true);
    setError(null);
    fetchDiscoverDetailContent(detailType, data)
      .then((content) => {
        setDetailContent(content);
      })
      .catch((err) => {
        console.error('Failed to load discover detail content', err);
        setError('Unable to load detailed insights right now. Please try again later.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [detailType, data]);

  const renderSectionHeader = () => {
    if (!data || !detailType) return null;

    switch (detailType) {
      case 'news':
        return (
          <div className="space-y-6 rounded-2xl">
            {data.image && (
              <div className="relative h-96 overflow-hidden rounded-2xl">
                <img src={data.image} alt={data.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <span
                className={cn(
                  'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium backdrop-blur',
                  isDarkMode ? 'bg-blue-500/15 text-blue-200' : 'bg-blue-100 text-blue-700'
                )}
              >
                <Newspaper className="h-4 w-4" />
                {data.source}
              </span>
              {data.publishedAt && (
                <span className={cn('inline-flex items-center gap-2 text-sm', isDarkMode ? 'text-slate-200' : 'text-gray-600')}>
                  <Calendar className="h-4 w-4" />
                  {new Date(data.publishedAt).toLocaleDateString()}
                </span>
              )}
              {detailContent?.metadata?.readingTime && (
                <span className={cn('inline-flex items-center gap-2 text-sm', isDarkMode ? 'text-slate-200' : 'text-gray-600')}>
                  <Clock className="h-4 w-4" />
                  {detailContent.metadata.readingTime}
                </span>
              )}
            </div>
            <div className="space-y-3">
              <h1 className={cn('text-4xl font-bold leading-tight', isDarkMode ? 'text-white' : 'text-slate-900')}>{data.title}</h1>
              <p className={cn('text-lg', isDarkMode ? 'text-slate-200' : 'text-slate-600')}>{data.description}</p>
            </div>
            {detailContent?.summary && (
              <div
                className={cn(
                  'rounded-3xl border p-6',
                  isDarkMode ? 'border-white/15 bg-slate-900/80 text-white' : 'border-slate-200 bg-white text-slate-900 shadow'
                )}
              >
                <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide opacity-70">
                  <Sparkles className="h-4 w-4" />
                  Summary
                </div>
                <p className={cn('mt-3 text-base leading-relaxed', isDarkMode ? 'text-slate-100 opacity-95' : 'opacity-90')}>{detailContent.summary}</p>
              </div>
            )}
          </div>
        );

      case 'topic':
        return (
          <div className="space-y-6">
            {data.image && (
              <div className="relative h-96 overflow-hidden rounded-4xl">
                <img src={data.image} alt={data.title} className="h-full w-full object-cover rounded-2xl" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <span
                className={cn(
                  'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium backdrop-blur-lg',
                  isDarkMode ? 'bg-purple-500/50 text-purple-200' : 'bg-purple-100 text-purple-700'
                )}
              >
                <Tag className="h-4 w-4" />
                {data.category}
              </span>
              {detailContent?.metadata?.tone && (
                <span className={cn('inline-flex items-center gap-2 text-sm', isDarkMode ? 'text-slate-200' : 'text-gray-600')}>
                  <Sparkles className="h-4 w-4" />
                  {detailContent.metadata.tone}
                </span>
              )}
            </div>
            <div className="space-y-3">
              <h1 className={cn('text-4xl font-bold leading-tight', isDarkMode ? 'text-white' : 'text-slate-900')}>{data.title}</h1>
              <p className={cn('text-lg', isDarkMode ? 'text-slate-200' : 'text-slate-600')}>{data.description}</p>
            </div>
            {detailContent?.summary && (
              <div
                className={cn(
                  'rounded-3xl border p-6 backdrop-blur-xl',
                  isDarkMode ? 'border-white/15 bg-slate-900/80 text-white' : 'border-slate-200 bg-white text-slate-900 shadow'
                )}
              >
                <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide opacity-70">
                  <Sparkles className="h-4 w-4" />
                  Overview
                </div>
                <p className={cn('mt-3 text-base leading-relaxed', isDarkMode ? 'text-slate-100 opacity-95' : 'opacity-90')}>{detailContent.summary}</p>
              </div>
            )}
          </div>
        );

      case 'weather':
        const metricCards = [
          {
            label: 'Temperature',
            value: `${data.temperature}°`,
            caption: data.condition,
          },
          data.feelsLike !== undefined
            ? {
                label: 'Feels Like',
                value: `${data.feelsLike}°`,
                caption: 'Apparent temperature',
              }
            : null,
          {
            label: 'High',
            value: `${data.high}°`,
            caption: 'Peak expected today',
          },
          {
            label: 'Low',
            value: `${data.low}°`,
            caption: 'Overnight minimum',
          },
          data.windSpeed !== undefined
            ? {
                label: 'Wind',
                value: `${data.windSpeed} km/h`,
                caption: data.windDirection || 'Current direction',
              }
            : null,
          data.humidity !== undefined
            ? {
                label: 'Humidity',
                value: `${data.humidity}%`,
                caption: 'Moisture level',
              }
            : null,
          data.pressure !== undefined
            ? {
                label: 'Pressure',
                value: `${data.pressure} mb`,
                caption: 'Barometric trend',
              }
            : null,
          data.visibility !== undefined
            ? {
                label: 'Visibility',
                value: `${data.visibility} km`,
                caption: 'Current distance',
              }
            : null,
          data.uv !== undefined
            ? {
                label: 'UV Index',
                value: `${data.uv}`,
                caption: 'Exposure risk',
              }
            : null,
        ].filter(Boolean) as { label: string; value: string; caption: string }[];

        return (
          <div className="space-y-6 rounded-2xl">
            <div
              className={cn(
                'rounded-3xl border py-6 px-2.5 sm:p-8 relative overflow-hidden',
                isDarkMode
                  ? 'border-white/10 bg-gradient-to-br from-slate-950 via-blue-950/70 to-blue-900/60 text-white'
                  : 'border-slate-200 bg-gradient-to-br from-sky-50 via-white to-blue-100 text-slate-900 shadow'
              )}
            >
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-24 right-0 h-64 w-64 rounded-full bg-yellow-400/10 blur-3xl" />
                <div className="absolute -bottom-16 left-10 h-48 w-48 rounded-full bg-blue-500/15 blur-3xl" />
              </div>
              <div className="relative z-10 flex flex-col gap-6">
                <div className="flex flex-col gap-4 sm:flex-row px-3 sm:items-center sm:justify-between">
                  <div className="space-y-2">
                    <div className="text-sm uppercase tracking-wide opacity-70">Weather details</div>
                    <h2 className="text-4xl font-semibold">
                      {data.temperature}° • {data.condition}
                    </h2>
                    <div className="flex flex-wrap items-center gap-3 text-sm opacity-70">
                      <span className="inline-flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {data.location}
                        {data.region && data.country && `, ${data.region}, ${data.country}`}
                      </span>
                      {data.localTime && (
                        <span>
                          Last update {new Date(data.localTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  </div>
                  <img
                    src={getWeatherArtForCondition(data.condition)}
                    alt={data.condition}
                    className={cn(
                      'pointer-events-none w-32 sm:w-40 md:w-48 opacity-90 animate-float',
                      isDarkMode ? 'mix-blend-screen' : 'mix-blend-multiply'
                    )}
                  />
                </div>

                <div className="flex flex-wrap gap-2 sm:gap-4">
                  {metricCards.map((metric) => (
                    <div
                      key={metric.label}
                      className={cn(
                        'rounded-[40px] flex justify-center items-center flex-col border px-4 py-4 backdrop-blur-sm transition-colors max-w-[150px]',
                        isDarkMode
                          ? 'border-white/10 bg-white/5 text-white'
                          : 'border-slate-200 bg-white/60 text-slate-900 shadow'
                      )}
                    >
                      <div className="text-xs font-semibold uppercase tracking-wide opacity-70">
                        {metric.label}
                      </div>
                      <div className="mt-2 text-2xl font-semibold">{metric.value}</div>
                      <div className="mt-1 text-center text-xs opacity-70">{metric.caption}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {data.forecast && data.forecast.length > 0 && (
              <div
                className={cn(
                  'rounded-3xl border p-0 overflow-hidden',
                  isDarkMode
                    ? 'border-white/10 bg-gradient-to-br from-slate-950 via-blue-950/60 to-blue-900/40 text-white'
                    : 'border-slate-200 bg-white text-slate-900 shadow'
                )}
              >
                <div className="px-6 pt-6 pb-2">
                  <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide opacity-70">
                    <Calendar className="h-4 w-4" />
                    Extended forecast
                  </div>
                </div>
                <div className="divide-y divide-white/5">
                  {data.forecast.slice(0, 7).map((day, idx) => {
                    const date = new Date(day.date);
                    const isToday = new Date().toDateString() === date.toDateString();
                    return (
                      <div
                        key={idx}
                        className={cn(
                          'grid grid-cols-[auto,1fr,auto,auto] items-center gap-3 px-6 py-4 text-sm',
                          isDarkMode ? (isToday ? 'bg-white/5' : 'hover:bg-white/5/60') : isToday ? 'bg-slate-100/70' : 'hover:bg-slate-50'
                        )}
                      >
                        <div className="font-medium">
                          {isToday ? 'Today' : date.toLocaleDateString('en-US', { weekday: 'short' })}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{day.icon}</span>
                          <span className="text-xs sm:text-sm opacity-80">{day.condition}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs opacity-70">
                          💧 {day.precipitation ?? 0}%
                        </div>
                        <div className="flex items-center gap-3 text-sm font-semibold">
                          <span>{day.maxTemp}°</span>
                          <span className="text-xs opacity-60">{day.minTemp}°</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {data.astronomy && (
              <div
                className={cn(
                  'rounded-3xl border p-6 sm:p-8',
                  isDarkMode ? 'border-white/10 bg-white/5 text-white' : 'border-slate-200 bg-white text-slate-900 shadow'
                )}
              >
                <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide opacity-70 mb-4">
                  <Sparkles className="h-4 w-4" />
                  Sun & moon
                </div>
                <div className="grid gap-4 sm:grid-cols-4 text-sm">
                  <div>
                    <div className="text-xs uppercase opacity-70">Sunrise</div>
                    <div className="mt-2 text-lg font-semibold">{data.astronomy.sunrise}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase opacity-70">Sunset</div>
                    <div className="mt-2 text-lg font-semibold">{data.astronomy.sunset}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase opacity-70">Moonrise</div>
                    <div className="mt-2 text-lg font-semibold">{data.astronomy.moonrise ?? '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase opacity-70">Moonset</div>
                    <div className="mt-2 text-lg font-semibold">{data.astronomy.moonset ?? '—'}</div>
                  </div>
                </div>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <div
                    className={cn(
                      'rounded-2xl border px-4 py-4',
                      isDarkMode ? 'border-white/10 bg-white/5 text-white' : 'border-slate-200 bg-slate-50 text-slate-900'
                    )}
                  >
                    <div className="text-xs uppercase opacity-70">Moon phase</div>
                    <div className="mt-2 text-xl font-semibold">{data.astronomy.moonPhase}</div>
                    {data.astronomy.moonIllumination !== undefined && (
                      <div className="mt-2 text-xs opacity-70">Illumination {data.astronomy.moonIllumination}%</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'digest':
        return (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <span
                className={cn(
                  'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium backdrop-blur',
                  isDarkMode ? 'bg-orange-500/15 text-orange-200' : 'bg-orange-100 text-orange-700'
                )}
              >
                <Calendar className="h-4 w-4" />
                {data.date}
              </span>
              {detailContent?.metadata?.readingTime && (
                <span className={cn('inline-flex items-center gap-2 text-sm', isDarkMode ? 'text-slate-200' : 'text-gray-600')}>
                  <Sparkles className="h-4 w-4" />
                  {detailContent.metadata.readingTime}
                </span>
              )}
            </div>
            <h1 className={cn('text-4xl font-bold leading-tight', isDarkMode ? 'text-white' : 'text-slate-900')}>{data.title}</h1>
            {detailContent?.summary && (
              <div
                className={cn(
                  'rounded-3xl border p-6',
                  isDarkMode ? 'border-white/15 bg-slate-900/80 text-white' : 'border-slate-200 bg-white text-slate-900 shadow'
                )}
              >
                <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide opacity-70">
                  <Sparkles className="h-4 w-4" />
                  Highlights
                </div>
                <p className={cn('mt-3 text-base leading-relaxed', isDarkMode ? 'text-slate-100 opacity-95' : 'opacity-90')}>{detailContent.summary}</p>
              </div>
            )}
          </div>
        );
    }
  };

  const renderKeyTakeaways = () => {
    if (!detailContent?.keyTakeaways?.length) return null;
    return (
      <section
        className={cn(
          'rounded-3xl border p-6 h-full flex flex-col',
          isDarkMode ? 'border-white/15 bg-gradient-to-br from-slate-900/60 via-slate-800/50 to-slate-900/70 text-white backdrop-blur-sm' : 'border-slate-200 bg-gradient-to-br from-slate-50 to-white text-slate-900 shadow-lg'
        )}
      >
        <div className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide opacity-70">
          <CheckCircle2 className="h-4 w-4" />
          Key takeaways
        </div>
        <div className="space-y-3 flex-1">
          {detailContent.keyTakeaways.map((point, idx) => (
            <div key={idx} className="flex gap-3 text-sm">
              <span className={cn('mt-1.5 h-2 w-2 rounded-full flex-shrink-0', isDarkMode ? 'bg-white/70' : 'bg-slate-500')} />
              <p className={cn('leading-relaxed', isDarkMode ? 'text-slate-100 opacity-95' : 'opacity-90')}>{point}</p>
            </div>
          ))}
        </div>
      </section>
    );
  };

  const renderStats = () => {
    if (!detailContent?.stats?.length) return null;
    return (
      <section
        className={cn(
          'rounded-3xl border p-6 h-full',
          isDarkMode ? 'border-white/15 bg-gradient-to-br from-slate-900/60 via-slate-800/50 to-slate-900/70 text-white backdrop-blur-lg' : 'border-slate-200 bg-gradient-to-br from-slate-50 to-white text-slate-900 shadow-lg'
        )}
      >
        <div className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide opacity-70">
          <Sparkles className="h-4 w-4" />
          Snapshot
        </div>
        <div className="flex flex-wrap gap-4">
          {detailContent.stats.map((stat, idx) => (
            <div
              key={`${stat.label}-${idx}`}
              className={cn(
                'rounded-2xl border px-4 py-3 transition-all max-w-[max-content]',
                isDarkMode ? 'border-white/15 bg-slate-900/70 hover:bg-slate-800/70' : 'border-slate-200 bg-white hover:shadow-md'
              )}
            >
              <div className="text-xs uppercase tracking-wide opacity-70">{stat.label}</div>
              <div className="mt-1 text-xl font-semibold">{stat.value}</div>
            </div>
          ))}
        </div>
      </section>
    );
  };

  const renderSections = () => {
    if (!detailContent?.sections?.length) return null;
    return (
      <section className="space-y-4 h-full">
        {detailContent.sections.map((section, idx) => (
          <article
            key={`${section.heading}-${idx}`}
            className={cn(
              'rounded-3xl border p-6 transition-all',
              isDarkMode ? 'border-white/15 bg-gradient-to-br from-slate-900/60 via-slate-800/50 to-slate-900/70 text-white backdrop-blur-sm hover:bg-slate-800/60' : 'border-slate-200 bg-gradient-to-br from-slate-50 to-white text-slate-900 shadow-lg hover:shadow-xl'
            )}
          >
            <h2 className={cn('text-xl font-semibold', isDarkMode ? 'text-white' : 'text-slate-900')}>{section.heading}</h2>
            <p className={cn('mt-3 text-sm leading-relaxed', isDarkMode ? 'text-slate-100 opacity-95' : 'opacity-90')}>{section.body}</p>
          </article>
        ))}
      </section>
    );
  };

  const renderResources = () => {
    if (!detailContent?.resources?.length) return null;
    return (
      <section
        className={cn(
          'rounded-3xl border p-6 h-full flex flex-col',
          isDarkMode ? 'border-white/15 bg-gradient-to-br from-slate-900/60 via-slate-800/50 to-slate-900/70 text-white backdrop-blur-sm' : 'border-slate-200 bg-gradient-to-br from-slate-50 to-white text-slate-900 shadow-lg'
        )}
      >
        <div className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide opacity-70">
          <ExternalLink className="h-4 w-4" />
          Additional resources
        </div>
        <div className="space-y-4 flex-1">
          {detailContent.resources.map((resource, idx) => (
            <div key={`${resource.title}-${idx}`} className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={cn('text-sm font-semibold', isDarkMode ? 'text-white' : 'text-slate-900')}>{resource.title}</span>
                {resource.url && resource.url !== '' && (
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'inline-flex items-center gap-1 text-xs font-semibold transition-colors',
                      isDarkMode ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-700'
                    )}
                  >
                    Visit
                    <ArrowUpRight className="h-3 w-3" />
                  </a>
                )}
              </div>
              <p className={cn('text-xs', isDarkMode ? 'text-slate-200 opacity-90' : 'opacity-80')}>{resource.description}</p>
            </div>
          ))}
        </div>
      </section>
    );
  };



  const renderQuote = () => {
    if (!detailContent?.quote?.text) return null;
    return (
      <section
        className={cn(
          'rounded-3xl border p-6 text-center h-full flex flex-col justify-center',
          isDarkMode ? 'border-white/15 bg-gradient-to-br from-purple-900/40 via-slate-900/70 to-slate-900/60 text-white backdrop-blur-sm' : 'border-slate-200 bg-gradient-to-br from-purple-50 to-white text-slate-900 shadow-lg'
        )}
      >
        <Quote className="mx-auto h-6 w-6 opacity-40" />
        <p className={cn('mt-3 text-lg leading-relaxed italic', isDarkMode ? 'text-slate-100 opacity-95' : 'opacity-90')}>"{detailContent.quote.text}"</p>
        {detailContent.quote.author && <p className={cn('mt-2 text-sm', isDarkMode ? 'text-slate-200 opacity-85' : 'opacity-70')}>— {detailContent.quote.author}</p>}
      </section>
    );
  };

  if (!data || !detailType || error) {
    return (
      <div className={cn('min-h-screen w-full flex items-center justify-center px-6 text-center', isDarkMode ? 'dark bg-black' : 'bg-white')}>
        <div>
          <h2 className={cn('text-2xl font-bold', isDarkMode ? 'text-white' : 'text-slate-900')}>Unable to load this content</h2>
          {error && <p className={cn('mt-2 text-sm opacity-70', isDarkMode ? 'text-slate-300' : 'text-slate-600')}>{error}</p>}
          <button
            onClick={() => navigate('/discover')}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Discover
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('min-h-screen w-full relative', isDarkMode ? 'dark' : '')}>
      <style>{`
        @keyframes floatWeatherArt { 0%, 100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-6px) rotate(0.5deg); } }
        .animate-float { animation: floatWeatherArt 5s ease-in-out infinite; }
      `}</style>
      <BackgroundWaves isDarkMode={isDarkMode} className="z-0" />
      
      <Sidebar
        isOpen={isOpen}
        onToggle={() => setIsOpen(!isOpen)}
        onNewChat={() => navigate('/')}
        isDarkMode={isDarkMode}
        onAuth={() => {}}
        user={user}
      />

      <div className="flex flex-col flex-1 relative min-h-screen">
        {/* Header */}
        <header
          className={cn(
            'relative top-0 z-40 transition-all duration-300 bg-transparent'
          )}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 z-50">
            <button
              onClick={() => navigate('/discover')}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-200',
                isDarkMode
                  ? 'text-white/80 hover:bg-white/10'
                  : 'text-gray-700 hover:bg-gray-100'
              )}
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 relative z-10 px-2 xs:px-4 sm:px-6 lg:px-8 py-12">
          <div className="mx-auto w-full max-w-7xl">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[200px]">
                <div className={cn('md:col-span-2 lg:col-span-3 md:row-span-2 animate-pulse rounded-3xl', isDarkMode ? 'bg-slate-900/60' : 'bg-slate-200')} />
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div key={idx} className={cn('animate-pulse rounded-3xl', isDarkMode ? 'bg-slate-900/50' : 'bg-slate-100')} />
                ))}
              </div>
            ) : (
              <div className="space-y-6">
                {renderSectionHeader()}

                {detailContent?.keyTakeaways?.length ? renderKeyTakeaways() : null}

                {detailContent?.stats?.length ? renderStats() : null}

                {detailContent?.sections?.length ? renderSections() : null}

                {detailContent?.quote?.text ? renderQuote() : null}

                {detailContent?.resources?.length ? renderResources() : null}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DiscoverDetail;
